import React from 'react';

/**
 * Visual primitives shared by all map components in this folder.
 *
 * Intentionally tiny: just the Mapbox token constant, a missing-token
 * placeholder, and the Pin SVG. Behavior (fitBounds, line rendering,
 * marker layout) belongs to the individual map components — this file
 * only provides shared *pixels*, not shared logic.
 */

export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export const MissingTokenPlaceholder: React.FC<{ height: number | string }> = ({ height }) => (
  <div
    style={{
      height,
      border: '1px dashed #e2e8f0',
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#64748b',
      fontSize: 13,
      padding: 12,
      textAlign: 'center',
    }}
  >
    Map disabled — set <code style={{ margin: '0 4px' }}>REACT_APP_MAPBOX_TOKEN</code> in
    the frontend <code style={{ marginLeft: 4 }}>.env</code> and restart the dev server.
  </div>
);

/**
 * Label rendering style. Two variants:
 *   • 'pill' — colored background with white text. Original default,
 *     used by the journey planner / friends / admin maps where the
 *     label needs to read clearly against any tile background.
 *   • 'text' — colored text with a white halo (no background). Used
 *     by the Trip CO2 Calculator's map hero, where the label is a
 *     city name and a pill behind it covers too much of the map.
 */
type PinVariant = 'pill' | 'text';

export const Pin: React.FC<{
  color: string;
  label: string;
  variant?: PinVariant;
}> = ({ color, label, variant = 'pill' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
    {variant === 'pill' ? (
      <div
        style={{
          background: color,
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.4,
          padding: '2px 6px',
          borderRadius: 4,
          marginBottom: 2,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
    ) : (
      <div
        style={{
          color,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.02,
          marginBottom: 2,
          whiteSpace: 'nowrap',
          // Multi-shadow white halo mirrors the look of Mapbox's
          // native text-halo-color symbol-layer styling.
          textShadow:
            '0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 5px #fff',
        }}
      >
        {label}
      </div>
    )}
    <svg width="20" height="28" viewBox="0 0 20 28" aria-hidden>
      <path
        d="M10 0C4.477 0 0 4.477 0 10c0 7 10 18 10 18s10-11 10-18c0-5.523-4.477-10-10-10z"
        fill={color}
      />
      <circle cx="10" cy="10" r="3.5" fill="#fff" />
    </svg>
  </div>
);
